import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { useTCoins, TCOINS_TYPES } from '../../context/TCoinsContext';
import { doc, updateDoc, arrayUnion, getDoc, increment } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { Z_INDEX_LEVELS, bringToFront } from '../../utils/zIndexManager';
import './GiftEffects.css';

const GiftCounter = ({ girlId, girlName, className = "" }) => {
  const { currentUser } = useAuth();
  const { tcoins, deductTCoins, getTotalTCoins } = useTCoins();
  const [showGifts, setShowGifts] = useState(false);
  const [isGiving, setIsGiving] = useState(false);
  const [giftCounts, setGiftCounts] = useState({
    car: 0,
    house: 0,
    hand: 0
  });
  const [clickEffects, setClickEffects] = useState({});

  // จัดการ z-index และ body scroll เมื่อ modal เปิด
  useEffect(() => {
    if (showGifts) {
      // ป้องกันการเลื่อนหน้า
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      
      // รอให้ DOM อัปเดต
      setTimeout(() => {
        const modalOverlay = document.querySelector('.gift-modal-overlay');
        const modalContent = document.querySelector('.gift-modal-content');
        
        if (modalOverlay) {
          bringToFront(modalOverlay, Z_INDEX_LEVELS.GIFT_MODAL);
          // ย้าย modal ไปยัง body เพื่อให้ออกมาด้านนอก
          document.body.appendChild(modalOverlay);
        }
        if (modalContent) {
          bringToFront(modalContent, Z_INDEX_LEVELS.GIFT_CONTENT);
        }
      }, 100);
    } else {
      // คืนค่า body scroll
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    
    // Cleanup function
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [showGifts]);

  const gifts = [
    {
      id: 'car',
      name: 'รถ',
      icon: '🚗',
      price: 100,
      description: 'ให้รถสวยๆ คันใหม่'
    },
    {
      id: 'house',
      name: 'บ้าน',
      icon: '🏠',
      price: 500,
      description: 'ให้บ้านหลังใหญ่'
    },
    {
      id: 'hand',
      name: 'มือ',
      icon: '👋',
      price: 50,
      description: 'ให้มือช่วยเหลือ'
    }
  ];

  // คำนวณราคารวม
  const getTotalPrice = () => {
    return Object.entries(giftCounts).reduce((total, [giftId, count]) => {
      const gift = gifts.find(g => g.id === giftId);
      return total + (gift ? gift.price * count : 0);
    }, 0);
  };

  const getTotalTCoinsAvailable = () => {
    return getTotalTCoins();
  };

  // คำนวณจำนวนของขวัญรวม
  const getTotalGifts = () => {
    return Object.values(giftCounts).reduce((total, count) => total + count, 0);
  };

  // เพิ่มจำนวนของขวัญ
  const incrementGift = (giftId) => {
    setGiftCounts(prev => ({
      ...prev,
      [giftId]: prev[giftId] + 1
    }));

    // เพิ่ม click effect
    setClickEffects(prev => ({
      ...prev,
      [giftId]: Date.now()
    }));

    // เพิ่ม sound effect (ถ้ามี)
    if (typeof window !== 'undefined' && window.Audio) {
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU5k9n1unEiBC13yO/eizEIHWq+8+OWT');
        audio.volume = 0.3;
        audio.play().catch(() => {});
      } catch (e) {
        // Ignore audio errors
      }
    }
  };

  // ลดจำนวนของขวัญ
  const decrementGift = (giftId) => {
    setGiftCounts(prev => ({
      ...prev,
      [giftId]: Math.max(0, prev[giftId] - 1)
    }));
  };

  // รีเซ็ตจำนวนทั้งหมด
  const resetGifts = () => {
    setGiftCounts({
      car: 0,
      house: 0,
      hand: 0
    });
  };

  const handleGiveGifts = async () => {
    // ตรวจสอบว่าไม่ใช่การให้ของขวัญตัวเอง
    if (currentUser.uid === girlId) {
      alert('ไม่สามารถให้ของขวัญตัวเองได้!');
      return;
    }

    const totalPrice = getTotalPrice();
    const totalGifts = getTotalGifts();

    if (totalGifts === 0) {
      alert('กรุณาเลือกของขวัญอย่างน้อย 1 ชิ้น!');
      return;
    }

    const totalTCoins = getTotalTCoinsAvailable();
    if (totalTCoins < totalPrice) {
      alert('T Coins ไม่เพียงพอ!');
      return;
    }

    setIsGiving(true);
    
    try {
      // หัก T Coins จากผู้ให้ (ใช้ Demo coins ก่อน)
      let remainingPrice = totalPrice;
      const coinTypes = [TCOINS_TYPES.DEMO, TCOINS_TYPES.SILVER, TCOINS_TYPES.GOLD, TCOINS_TYPES.EMERALD, TCOINS_TYPES.ONYX, TCOINS_TYPES.PURPLE];
      
      for (const coinType of coinTypes) {
        if (remainingPrice <= 0) break;
        if (tcoins[coinType] > 0) {
          const deductAmount = Math.min(remainingPrice, tcoins[coinType]);
          await deductTCoins(currentUser.uid, coinType, deductAmount, `ให้ของขวัญ ${girlName}`);
          remainingPrice -= deductAmount;
        }
      }
      
      // สร้างรายการของขวัญที่จะให้
      const giftsToGive = [];
      Object.entries(giftCounts).forEach(([giftId, count]) => {
        if (count > 0) {
          const gift = gifts.find(g => g.id === giftId);
          for (let i = 0; i < count; i++) {
            giftsToGive.push({
              id: `${Date.now()}_${giftId}_${i}`,
              giftType: gift.id,
              giftName: gift.name,
              giftIcon: gift.icon,
              price: gift.price,
              fromUserId: currentUser.uid,
              fromUserName: currentUser.displayName || currentUser.email,
              timestamp: new Date(),
              message: `ได้รับ${gift.name}จาก ${currentUser.displayName || currentUser.email}`
            });
          }
        }
      });

      // บันทึกการให้ของขวัญในโปรไฟล์น้อง
      const girlRef = doc(db, 'girls', girlId);
      await updateDoc(girlRef, {
        gifts: arrayUnion(...giftsToGive)
      });

      // เพิ่ม T Coins ให้กับน้องที่ได้รับของขวัญ (ใช้ Demo coins)
      await deductTCoins(girlId, TCOINS_TYPES.DEMO, -totalPrice, `ได้รับของขวัญจาก ${currentUser.displayName || currentUser.email}`);

      // บันทึกประวัติการให้ของขวัญของลูกค้า
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        giftHistory: arrayUnion({
          id: Date.now().toString(),
          giftType: 'multiple',
          giftName: `ของขวัญ ${totalGifts} ชิ้น`,
          giftIcon: '🎁',
          price: totalPrice,
          toUserId: girlId,
          toUserName: girlName,
          timestamp: new Date(),
          details: giftCounts
        })
      });

      alert(`ให้ของขวัญ ${totalGifts} ชิ้น (${totalPrice} T Coins) ให้${girlName}เรียบร้อยแล้ว! 🎁`);
      setShowGifts(false);
      resetGifts();
      
    } catch (error) {
      console.error('Error giving gifts:', error);
      alert('เกิดข้อผิดพลาดในการให้ของขวัญ');
    } finally {
      setIsGiving(false);
    }
  };

  // ตรวจสอบว่าไม่ใช่ตัวเอง
  const isSelf = currentUser && currentUser.uid === girlId;

  return (
    <div className={`relative ${className}`}>
      {/* ปุ่มให้ของขวัญ - แสดงเฉพาะเมื่อไม่ใช่ตัวเอง */}
      {!isSelf && (
        <button
          onClick={() => setShowGifts(!showGifts)}
          className="flex items-center space-x-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg"
        >
          <span className="text-lg">🎁</span>
          <span className="font-medium">ให้ของขวัญ</span>
          <span className="text-sm">{showGifts ? '▲' : '▼'}</span>
        </button>
      )}

      {/* Modal ของขวัญ - ใช้ Portal ออกมาด้านนอก */}
      {showGifts && createPortal(
        <div 
          className="fixed inset-0 gift-modal-overlay flex items-center justify-center"
          style={{ 
            zIndex: Z_INDEX_LEVELS.GIFT_MODAL,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh'
          }}
        >
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            style={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%'
            }}
            onClick={() => setShowGifts(false)}
          />
          
          {/* Modal */}
          <div 
            className="relative bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 gift-modal-content gift-modal-entrance"
            style={{ 
              zIndex: Z_INDEX_LEVELS.GIFT_CONTENT,
              position: 'relative',
              margin: 'auto',
              maxWidth: '500px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">🎁 ให้ของขวัญ: {girlName}</h3>
              <button
                onClick={() => setShowGifts(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">T Coins ที่มี: <span className="font-bold text-blue-600">{getTotalTCoinsAvailable()}</span></p>
              <p className="text-sm text-gray-600">ราคารวม: <span className={`font-bold text-green-600 gift-total-price ${getTotalPrice() > 0 ? 'increase' : ''}`}>{getTotalPrice()}</span> T Coins</p>
              <p className="text-sm text-gray-600">จำนวนรวม: <span className="font-bold text-purple-600">{getTotalGifts()}</span> ชิ้น</p>
            </div>

            <div className="space-y-3 mb-6">
              {gifts.map((gift) => (
                <div
                  key={gift.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <button
                        onClick={() => incrementGift(gift.id)}
                        className={`text-4xl transform transition-all duration-200 hover:scale-110 gift-button-hover ${
                          clickEffects[gift.id] && Date.now() - clickEffects[gift.id] < 300
                            ? 'gift-icon-click'
                            : ''
                        }`}
                      >
                        {gift.icon}
                      </button>
                      {giftCounts[gift.id] > 0 && (
                        <div className={`absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold gift-counter-badge ${
                          clickEffects[gift.id] && Date.now() - clickEffects[gift.id] < 300
                            ? 'gift-icon-bounce'
                            : ''
                        }`}>
                          {giftCounts[gift.id]}
                        </div>
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-lg">{gift.name}</h4>
                      <p className="text-sm text-gray-600">{gift.description}</p>
                      <p className="text-sm text-blue-600 font-medium">{gift.price} T Coins/ชิ้น</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => decrementGift(gift.id)}
                      disabled={giftCounts[gift.id] === 0}
                      className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full hover:bg-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      -
                    </button>
                    <span className="w-8 text-center font-bold">{giftCounts[gift.id]}</span>
                    <button
                      onClick={() => incrementGift(gift.id)}
                      className="w-8 h-8 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors flex items-center justify-center"
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={resetGifts}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                รีเซ็ต
              </button>
              <button
                onClick={handleGiveGifts}
                disabled={isGiving || getTotalGifts() === 0 || getTotalTCoinsAvailable() < getTotalPrice()}
                className="flex-1 py-2 px-4 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isGiving ? 'กำลังให้...' : `ให้ ${getTotalGifts()} ชิ้น`}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default GiftCounter;
